import { Container } from '@almbrand/container';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { Component } from 'components/Component';
import { PageContext } from 'context/PageContext';
import { useFormSubmit, useModal } from 'hooks';
import { useContext, useEffect, useState } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormResetField,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';
import { useAppDispatch } from 'store/hooks';
import { setDisplayGlobalError } from 'store/slices/errorSlice';
import { getDependencyFieldIds, meetDependency } from '../../../services/DependencyService';
import { getComponentProperties } from '../../../services/MapFieldService';
import { isPackageStepByPageId } from '../../../services/PackageService';
import { SystemIconsComponent } from '../SystemIconsComponent';
import styles from './ModalContentComponent.module.scss';

export interface ModalContentComponentProps extends FieldsBlock {
	resetField?: UseFormResetField<FieldValues>;
	register?: UseFormRegister<FieldValues>;
	watch?: UseFormWatch<FieldValues>;
	isValid?: boolean;
	hasError?: boolean;
	errors?: FieldErrors<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	errorMessage?: string;
	flowStop?: boolean;
	action?: { title?: string; subtitle?: string; content?: string };
	linkText?: string;
	infoIcon?: boolean;
	subtitle?: string;
	fields?: Fields;
	pageId?: string;
	contentType?: string;
	contentId?: number;
	isTextColorWhite?: boolean;
	linkTextAlignment?: 'left' | 'center' | 'right';
}
export const ModalContentComponent: React.FC<ModalContentComponentProps> = ({
	resetField,
	register,
	watch = (e) => {
		return undefined;
	},
	isValid,
	errors,
	trigger,
	errorMessage,
	flowStop,
	contentDependency,
	action,
	linkText,
	infoIcon,
	title,
	subtitle,
	content,
	fields,
	pageId,
	isTextColorWhite,
	linkTextAlignment = 'left',
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext;

	const { displayModal } = useModal();
	const [stepError, setStepError] = useState<string>();
	const dependencyFieldIds = getDependencyFieldIds(contentDependency);
	const dependentFields = dependencyFieldIds.map((fieldId) => watch(fieldId));
	const dependencyValuesKey = JSON.stringify(
		dependentFields.map((field) => (field && typeof field === 'object' && 'value' in field ? field.value : field))
	);
	const { resetFormField } = useFormSubmit();
	const dispatch = useAppDispatch();
	const isPackages = isPackageStepByPageId(Number.parseInt(pageId));

	const { isMobile } = useDeviceQuery();

	useEffect(() => {
		// TODO(CD-08): Clear stale restriction errors when all watched answers disappear instead of returning early.
		if (!dependentFields.some(Boolean) || !(action?.content || action?.title || action?.subtitle)) {
			return;
		}
		const dependencyValues = Object.fromEntries(
			dependencyFieldIds.map((fieldId, index) => {
				const field = dependentFields[index];
				return [fieldId, field && typeof field === 'object' && 'value' in field ? field.value : field];
			})
		);
		if (meetDependency(contentDependency, dependencyValues)) {
			displayModal({ title: action.title, subtitle: action.subtitle, content: action.content });
			if (flowStop) {
				setStepError(errorMessage);
				// TODO(CD-09): Confirm whether OR flow stops should reset all referenced fields or only the triggering branch.
				dependentFields.forEach((field, index) => {
					const fieldId = field?.id ?? dependencyFieldIds[index];
					if (fieldId) {
						resetField(fieldId);
						resetFormField(fieldId);
					}
				});
				dispatch(setDisplayGlobalError(true));
			}
		} else {
			dispatch(setDisplayGlobalError(false));
			setStepError(undefined);
		}
	}, [dependencyValuesKey]);

	const mapComponents = () => {
		return fields?.items?.map((contentItem, index) => {
			const name = contentItem.content.uniqueIdentifier?.uid ?? 'NO_UNIQUE_ID_FOR_CONTENT_ITEM';

			// these formFields (if they are such) will be part of the form and therefore part of form validation!
			// This behavior we will have to look into
			const updatedComponentProps = getComponentProperties(pageId, contentItem, index, {
				register,
				resetField,
				watch,
				isValid,
				hasError: !!errors?.[name],
				errors,
				trigger,

				// submitStep: () => handleSubmit(onSubmit)(), // insert this if onchange submit form
			});

			return updatedComponentProps ? (
				<Component
					key={contentItem.content.contentId}
					{...updatedComponentProps}
				/>
			) : undefined;
		});
	};

	return (
		<>
			{(!isPackages || (isPackages && !isMobile)) && linkText && (
				<Container
					mobileWidth={isMobile ? 'fullWidth' : '342'}
					tabletWidth={isMobile ? 'fullWidth' : '996'}
					desktopWidth={isMobile ? 'fullWidth' : '996'}
					noPadding={!isMobile}
					className={classNames(styles.ModalContentComponent__container)}
				>
					<button
						type='button'
						className={classNames(
							styles.ModalContentComponent__link,
							(isPackages || isTextColorWhite) && styles.ModalContentComponent__link__packages,
							styles[`ModalContentComponent__link--align-${linkTextAlignment}`]
						)}
						onClick={(e) => {
							e.preventDefault();
							displayModal({ title, subtitle, content, fields: mapComponents() });
						}}
					>
						{
							<>
								<div>{linkText}</div>
								{infoIcon && (
									<SystemIconsComponent
										icon='Info'
										height='16'
										themeName={theme}
									/>
								)}
							</>
						}
					</button>
				</Container>
			)}
			{action?.title && stepError && (
				<p
					role='alert'
					className={styles.ModalContentComponent__error}
				>
					{stepError}
				</p>
			)}
		</>
	);
};
